import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, ClampLines } from '@resy/ui-kitchen';
import getImage from '../../AmexMarketingUtils/getAmexImage';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { EVENT_TYPES } from '../constants';
import { CTA_EVENT_NAME, EVENT_CONTEXT } from '../../AmexMarketingUtils/constants';
import { getRecurringEventImage } from '../AmexCenturionNYDetails';
import AmexEventImagePreview from '../../AmexMarketingUtils/AmexEventImagePreview';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';

import './AmexCenturionNYEventCard.scss';

const AmexCenturionNYEventCard = (props) => {
  const {
    eventButtons,
    date,
    description,
    end_time: endTime,
    event_type: eventType,
    image_preview: imagePreview,
    name,
    time,
    url_slug: urlSlug,
    event_type_id: eventTypeId,
    day_date_time_display: displayText,
    date_preview_content: previewContent = {},
    responsive_images: images,
    baseRequestUrl,
    isDesktop,
    useCustomImage,
    Analytics,
    hideMoreInfoCta,
    venue,
    eventDetails,
    eventContext,
    featuredPreviewText,
    isFeaturedEvent,
    tabHeading,
  } = props;

  if (!eventTypeId && (!urlSlug)) {
    return null;
  }

  const getFormattedDateTime = () => {
    moment.tz.setDefault('America/New_York');
    const endTimeFormatted = (endTime && eventType !== 1) ? ` - ${moment(endTime, 'HH:mm').format('LT')}` : '';

    const startToEnd = `${moment(`${date} ${time}`)
      .format('dddd, MMMM D[,] YYYY, [<br />][at] h:mm A')}${endTimeFormatted}`;

    const sanitizedStartToEnd = sanitizeString(startToEnd);

    if (!endTimeFormatted) {
      return (
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizedStartToEnd,
          }}
        />
      );
    }

    return (
      <ClampLines
        text={startToEnd}
        id={`event-card-${date}-${startToEnd}`}
        lines={2}
        buttons={false}
      />
    );
  };

  const eventButton = Array.isArray(eventButtons) && eventButtons?.filter((item) => {
    if (item.event_type_id === eventTypeId) {
      return item.button_label;
    }
    return null;
  });

  const getActionName = (eventInfo) => {
    let actionName;
    switch (eventInfo) {
      case 'Upcoming':
        actionName = 'Upcoming Events - Purchase Tickets Click';
        break;
      case 'Featured':
        actionName = 'Featured Event - Book Now Click';
        break;
      case 'Notify':
        actionName = 'Upcoming Events - Join Notify List Click';
        break;
      case 'Past':
        actionName = 'More Info Click';
        break;
      default:
        break;
    }
    return actionName;
  };

  const currentEventClick = (eventUrl, eventInfo) => {
    const extraParams = {
      tab_item: tabHeading,
      item_name: name,
      action_name: getActionName(eventInfo),
    };
    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
    window.open(eventUrl, '_blank');
  };

  if (eventTypeId === EVENT_TYPES.WALK_IN) {
    const eventImage = getRecurringEventImage(images, useCustomImage);
    const sanitizeName = sanitizeString(name);
    const sanitizeDisplayText = sanitizeString(displayText);
    const sanitizeDescription = sanitizeString(description);

    return (
      <div
        className="AmexCenturionNYEventCard"
      >
        <div
          className={`AmexCenturionNYEventCard__image recurring-image ${useCustomImage ? 'custom-image' : ''}`}
          style={{
            backgroundImage: `url(${baseRequestUrl}CenturionNY/${eventImage})`,
          }}
        />
        <div className="AmexCenturionNYEventCard__copy-wrapper">
          <div className="AmexCenturionNYEventCard__copy">
            {name && (
              <h4
                className="AmexCenturionNYEventCard__title recurringEvent"
                dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                  __html: sanitizeName,
                }}
              />
            )}
            <AmexEventImagePreview
              previewContent={previewContent}
            />
            {displayText && (
              <p
                className="AmexCenturionNYEventCard__date"
                dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                  __html: sanitizeDisplayText,
                }}
              />
            )}
            {description && (
              <div
                className="AmexCenturionNYEventCard__description"
                dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                  __html: sanitizeDescription,
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
  if (eventTypeId === EVENT_TYPES.API) {
    const {
      is_sold_out_enabled: isSoldOutEnabled,
      sold_out_label: soldOutLabel,
    } = eventDetails;

    const {
      sold_out: soldOut,
    } = props;

    const getEventDetails = () => {
      const {
        show_notify: showNotify,
        notify,
      } = props;

      const {
        event_tag_info: eventTagInfo,
      } = eventDetails;

      let ctaLabel = '';
      let eventInfo = '';

      if (eventContext === EVENT_CONTEXT.PAST_EVENTS) {
        const { label } = eventButtons;
        ctaLabel = label;
        eventInfo = 'Past';
      } else if (isFeaturedEvent) {
        const { button_label: buttonLabels } = eventButton[0] || {};
        ctaLabel = buttonLabels?.featured;
        eventInfo = eventTagInfo?.featured;
      } else {
        const { button_label: buttonLabels } = eventButton[0] || {};
        ctaLabel = buttonLabels?.purchase_tickets;
        eventInfo = eventTagInfo?.purchase_tickets;

        const { hasNotify } = notify;
        if (isSoldOutEnabled) {
          if (showNotify && !hasNotify) {
            ctaLabel = buttonLabels?.join_notify_list;
            eventInfo = eventTagInfo?.join_notify_list;
          } else if (showNotify && hasNotify) {
            ctaLabel = buttonLabels?.on_notify_list;
            eventInfo = eventTagInfo?.on_notify_list;
          } else if (!showNotify) {
            ctaLabel = buttonLabels?.purchase_tickets;
            eventInfo = eventTagInfo?.purchase_tickets;
          }
        }
      }
      return {
        ctaLabel, eventInfo,
      };
    };

    const { ctaLabel, eventInfo } = getEventDetails();
    const renderSoldOutLabel = () => {
      if ((eventContext !== EVENT_CONTEXT.PAST_EVENTS) && soldOut && isSoldOutEnabled) {
        return (
          <p className="AmexCenturionNYEventCard__soldOutLabel">
            {soldOutLabel}
          </p>
        );
      }
      return null;
    };

    const apiImage = getImage(images, imagePreview, isDesktop);
    const eventHref = `cities/${venue.location?.urlSlug}/venues/${venue.urlSlug}/events/${urlSlug}`;
    const HeadingTag = isFeaturedEvent || eventContext === EVENT_CONTEXT.PAST_EVENTS ? 'h3' : 'h4';
    return (
      <div className={cx('AmexCenturionNYEventCardWrapper',
        { pastEvent: eventContext === EVENT_CONTEXT.PAST_EVENTS })}
      >
        <div className="AmexCenturionNYEventCard">
          <div
            className={cx('AmexCenturionNYEventCard__image',
              {
                soldout: (soldOut && isSoldOutEnabled
            && eventContext !== EVENT_CONTEXT.PAST_EVENTS),
              })}
            style={{
              backgroundImage: `url(${apiImage})`,
            }}
          >
            {renderSoldOutLabel()}
          </div>
          <div className={cx('AmexCenturionNYEventCard__copy-wrapper',
            { pastEvent: eventContext === EVENT_CONTEXT.PAST_EVENTS })}
          >
            <div className={cx('AmexCenturionNYEventCard__copy',
              { pastEvent: eventContext === EVENT_CONTEXT.PAST_EVENTS })}
            >
              <HeadingTag
                className={cx('AmexCenturionNYEventCard__title',
                  {
                    pastEvent: eventContext === EVENT_CONTEXT.PAST_EVENTS,
                    featured: isFeaturedEvent,
                  })}
              >
                {name}
              </HeadingTag>
              <AmexEventImagePreview
                date={date}
              />
              {isFeaturedEvent && (
              <p className="AmexCenturionNYEventCard__description">
                {featuredPreviewText}
              </p>
              )}
              <div className="AmexCenturionNYEventCard__footer">
                {(eventContext !== EVENT_CONTEXT.PAST_EVENTS) && (
                <p className={`AmexCenturionNYEventCard__date ${isFeaturedEvent ? 'featured' : ''}`}>
                  {getFormattedDateTime()}
                </p>
                )}
                {(ctaLabel && !hideMoreInfoCta) && (
                <Button
                  className={cx('AmexCenturionNYEventCard__cta',
                    { pastEvent: eventContext === EVENT_CONTEXT.PAST_EVENTS })}
                  aria-label={`${name} ${ctaLabel}`}
                  onClick={() => currentEventClick(eventHref, eventInfo)}
                >
                  {ctaLabel}
                </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

AmexCenturionNYEventCard.propTypes = {
  eventButtons: PropTypes.arrayOf(PropTypes.shape({
    event_type_id: PropTypes.number.isRequired,
    button_label: PropTypes.string.isRequired,
  })).isRequired,
  date: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  end_time: PropTypes.string.isRequired,
  event_type: PropTypes.number.isRequired,
  image_preview: PropTypes.shape({}).isRequired,
  name: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  url_slug: PropTypes.string.isRequired,
  event_type_id: PropTypes.number.isRequired,
  day_date_time_display: PropTypes.string,
  date_preview_content: PropTypes.shape({}),
  responsive_images: PropTypes.shape({
    '16:9': PropTypes.string.isRequired,
  }).isRequired,
  baseRequestUrl: PropTypes.string,
  isDesktop: PropTypes.bool.isRequired,
  useCustomImage: PropTypes.bool.isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  venue: PropTypes.shape({
    locality: PropTypes.string,
    location: PropTypes.shape({
      urlSlug: PropTypes.string,
    }),
    name: PropTypes.string.isRequired,
    neighborhood: PropTypes.string.isRequired,
    urlSlug: PropTypes.string.isRequired,
  }).isRequired,
  eventDetails: PropTypes.shape({
    is_sold_out_enabled: PropTypes.bool,
    sold_out_label: PropTypes.string,
    event_tag_info: PropTypes.shape({
      purchase_tickets: PropTypes.string,
      join_notify_list: PropTypes.string,
      on_notify_list: PropTypes.string,
      featured: PropTypes.string,
    }),
  }),
  show_notify: PropTypes.bool,
  sold_out: PropTypes.bool,
  notify: PropTypes.shape({
    hasNotify: PropTypes.bool,
  }),
  eventContext: PropTypes.string,
  featuredPreviewText: PropTypes.string,
  isFeaturedEvent: PropTypes.bool,
  hideMoreInfoCta: PropTypes.bool,
  tabHeading: PropTypes.string.isRequired,
};

AmexCenturionNYEventCard.defaultProps = {
  baseRequestUrl: '',
  day_date_time_display: undefined,
  date_preview_content: {},
  eventDetails: {
    is_sold_out_enabled: false,
    event_tag_info: {},
  },
  show_notify: false,
  sold_out: false,
  notify: {},
  eventContext: '',
  featuredPreviewText: '',
  isFeaturedEvent: false,
  hideMoreInfoCta: false,
};

export default AmexCenturionNYEventCard;
